/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { SET_QUOTE_TEMPLATE_LINE_ITEM_NOTE_MUTATION } from './graphql/setQuoteTemplateLineItemNote';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface AddQuoteTemplateLineItemNoteParams {
  templateId: string;
  itemId: string;
  note?: string;
}

export const addQuoteTemplateLineItemNote = async (
  params: AddQuoteTemplateLineItemNoteParams
) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!params.itemId) {
    throw new Error('Item ID is required');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    const results: any = await fetchGraphQl(
      SET_QUOTE_TEMPLATE_LINE_ITEM_NOTE_MUTATION,
      {
        variables: {
          input: {
            templateId: params.templateId,
            item_uid: params.itemId,
            note: params.note,
          },
        },
      }
    );

    if (!results?.data?.setQuoteTemplateLineItemNote) {
      throw new Error('No quote template data received');
    }

    const transformedData = transformQuoteTemplate(
      results.data.setQuoteTemplateLineItemNote
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
